import { BlocksRoundCreator } from './blocks-round-creator';
import { Subject } from 'rxjs';
import { Move } from '../logic/models/move';
import { BlocksListener } from '../game/blocks-listener';
import { BlocksRemoteRoundParams } from './blocks-remote-round-params';
import { BlocksLocalRoundParams } from './blocks-local-round-params';

export class BlocksGameRunner {
  public get onGameSetupStartTrigger() {
    return this.onGameSetupStart.asObservable();
  }
  public get onGameSetupCompleteTrigger() {
    return this.onGameSetupComplete.asObservable();
  }
  public get blocksLogicActionBus$() {
    return this.roundCreator.blocksLogicActionBus$;
  }
  public get currentGame() {
    return this.roundCreator.currentRound?.currentGame;
  }

  private readonly gameListener: BlocksListener;
  private readonly roundCreator: BlocksRoundCreator;
  private readonly onGameSetupStart: Subject<void>;
  private readonly onGameSetupComplete: Subject<void>;

  constructor(gameListener: BlocksListener) {
    this.gameListener = gameListener;
    this.roundCreator = new BlocksRoundCreator(gameListener);
    this.onGameSetupStart = new Subject();
    this.onGameSetupComplete = new Subject();
  }

  public startNewRemoteRound(logicVersion: number) {
    this.roundCreator.startNewRound(
      BlocksRemoteRoundParams.create(this.gameListener, logicVersion),
    );

    this.onGameSetupStart.next();
    void this.newGameSetup();
  }

  public startExistingRemoteRound(moves: string[], logicVersion: number) {
    this.roundCreator.startNewRound(
      BlocksRemoteRoundParams.create(this.gameListener, logicVersion),
    );

    this.onGameSetupStart.next();
    void this.existingGameSetup(moves);
  }

  public startNewLocalRound(logicVersion: number) {
    this.roundCreator.startNewRound(BlocksLocalRoundParams.create(this.gameListener, logicVersion));

    this.onGameSetupStart.next();
    void this.newGameSetup();
  }

  public clickStartGame() {
    this.gameListener.onStartGame();
  }

  public setInstantFinish() {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }
    game.setInstantFinish();
  }

  private async newGameSetup(): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    game.nextRound();

    this.onGameSetupComplete.next();

    this.roundCreator.currentRound?.onGameSetupAnimationFinished();
  }

  private async existingGameSetup(moves: string[]): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    game.nextRound();

    const commands = moves.map((move) => Move.stringToMove(move));

    for (const command of commands) {
      await game.waitForAllTasksToBeCompleted();

      if (command !== undefined) {
        game.execute(command, true);
      }
    }

    this.onGameSetupComplete.next();

    this.roundCreator.currentRound?.onGameSetupAnimationFinished();
  }
}
